import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "../../hooks/useRouter";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import "./TalentPulse360.css";

// components
import Dashboard from "../../components/Dashboard/Dashboard";
import ChatPromptContainer from "../../components/ChatPromptContainer/ChatPromptContainer";
import MessageBox from "../../components/MessageBox/MessageBox";
import LoadingScreen from "../../components/LoadingScreen/LoadingScreen";
import Modal from "../../components/Modal/Modal";
import TalentPulseFilterBar from "../../components/TalentPulseFilterBar/TalentPulseFilterBar";
import TalentPulseProfileCard from "../../components/TalentPulseProfileCard/TalentPulseProfileCard";

// store
import {
    fetchTalentPulseCompletions,
    fetchTalentPulseUserConversation,
    updateTalentPulseFeedback,
    stopTalentPulseChatStream,
    handleTalentPulseCitationDownload,
    updateTalentPulseWebSearchConsent,
    fetchTalentPulseProfileFilters,
    fetchTalentPulseProfiles,
    fetchTalentPulseProfileDetail,
    getSpeechWsUrl,
} from "../../store/apiActions";

// utility & constants
import useIsMobile from "../../hooks/useIsMobile";
import { useAuth } from "../../hooks/useAuth";
import { useEventEmitter } from "../../contexts/EventEmmiter";
import { useConstants } from "../../constant/constantProvider";
import {
    scrollToLatestViewPoint,
    generateCustomUUID,
    generateSessionId
} from "../../utils/commonHelper";
import { sessionManager, encrypted } from "../../utils/utility";
import { handleUnauthorized } from "../../utils/apiHelper";
import { ROUTES, SSE_EVENTS, CONSENT_STATUS } from "../../constant/keyConstants";
import {
    formatTalentPulseContent,
    getNonTranscriptCitations,
    renderCitationPanels,
    renderTalentPulseContent,
    extractFinalResponseFromTalentPulse,
    handleTalentPulseStreamEvent,
    getNormalizedCareerTimeline,
} from "../../utils/talentPulseUtils";
import textToSpeech, { computeSpokenDuration } from "../../utils/textToSpeech";
import useAlexaMode from "../../hooks/useAlexaMode";
import { downloadBlob } from "../../utils/fileHelper";
import { UPL_GENIE_PROMPT_CHAR_LIMIT } from "../../constant/globalConstants";

// images
import bulbIcon from "../../assets/new-images/purple-bulb-icon.svg";
import downArrow from "../../assets/new-images/dropdown-icon.svg";
import talentPulse360Icon from "../../assets/icons/app-icons/talent-pulse-360-logo.svg";
import emptyProfilesIcon from "../../assets/new-images/talent-pulse-empty-profiles.svg";
import { setTalentPulseWebSearchEnabled } from "../../store/actions";



/**
 * Feature flag: controls access to candidate profiles.
 *
 * Currently set to `true` for ALL users (no RBAC yet).
 * When RBAC is implemented, replace this with a permission check:
 *   const CANDIDATE_PROFILES_ENABLED = user.permissions.includes('candidate_profiles');
 *
 * When `false`:
 *  - The "Profiles" feature is hidden.
 *  - The `type=external` query parameter is never sent to the profiles API.
 *  - All profile filter APIs still run (fetching internal/all profiles).
 */
const CANDIDATE_PROFILES_ENABLED = true;

// Profile APIs use candidate IDs as their canonical identifiers, while the
// card list may receive them with different casing. Keep selection state in
// one normalized format so restored selections match their cards after reload.
const normalizeCandidateId = (id) => String(id ?? "").trim().toLowerCase();

/** Skeleton placeholder card — matches real TalentPulseProfileCard layout */
const ProfileCardSkeleton = () => (
    <div className="tpc-skeleton-card" aria-hidden="true">
        {/* Header: avatar + name/badge + checkbox */}
        <div className="tpc-skeleton-header">
            <div className="tpc-skeleton-header-left">
                <div className="tpc-sk tpc-sk-avatar" />
                <div className="tpc-skeleton-info">
                    <div className="tpc-sk tpc-sk-name" />
                    <div className="tpc-sk tpc-sk-badge" />
                </div>
            </div>
            <div className="tpc-sk tpc-sk-checkbox" />
        </div>
        {/* Candidate ID + detail rows */}
        <div className="tpc-skeleton-details">
            <div className="tpc-sk tpc-sk-id" />
            <div className="tpc-sk tpc-sk-detail" />
            <div className="tpc-sk tpc-sk-detail short" />
        </div>
        {/* Company / Experience / Source grid */}
        <div className="tpc-skeleton-grid">
            {['Company', 'Experience', 'Source'].map((col) => (
                <div className="tpc-skeleton-grid-col" key={col}>
                    <div className="tpc-sk tpc-sk-label" />
                    <div className="tpc-sk tpc-sk-value" />
                </div>
            ))}
        </div>
        {/* View full profile link */}
        <div className="tpc-skeleton-footer">
            <div className="tpc-sk tpc-sk-link" />
        </div>
    </div>
);


const REGION_BY_STATE = {
    Haryana: 'North',
    Punjab: 'North',
    Rajasthan: 'North',
    UP: 'North',
    MP: 'Central',
    Maharashtra: 'West',
    Gujarat: 'West',
    AP: 'South'
};

const getCandidateRole = (candidate) => candidate.details[0]?.text
    ?.split('·')[0]
    .replace(/\s*\(applied\)/i, '')
    .trim() || 'Other';

const getCandidateState = (candidate) => {
    const location = candidate.details.find((detail) => detail.icon)?.text || '';
    return location.split('·')[0].split(', ').pop().trim();
};

const getCandidateRegion = (candidate) => REGION_BY_STATE[getCandidateState(candidate)] || 'Other';
const getCandidateTerritory = (candidate) => `${getCandidateRegion(candidate)} Zone`;

const ProfileListEmptyState = ({ title, description }) => (
    <div className="talent-pulse-empty-list" aria-live="polite">
        <div className="talent-pulse-empty-icon talent-pulse-empty-people">
            <img src={emptyProfilesIcon} alt="" />
        </div>
        <strong>{title}</strong>
        {description && <p>{description}</p>}
    </div>
);

const TalentPulse360 = () => {
    const { username, fullName } = useAuth();
    const isMobile = useIsMobile();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { subscribe, emit } = useEventEmitter();
    const constants = useConstants();

    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isChatLoading, setIsChatLoading] = useState(false);
    const [uiConversation, setUIConversation] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editedText, setEditedText] = useState("");

    const alexaActiveRef = useRef(false);
    const [autoPlayLastMessage, setAutoPlayLastMessage] = useState(false);
    const autoPlayLastMessageRef = useRef(false);

    const [feedbackInput, setFeedbackInput] = useState({});
    const [submittingFeedback, setSubmittingFeedback] = useState(false);

    const [showWebConsent, setShowWebConsent] = useState(false);

    // Filter Bar & API State
    const [filterType, setFilterType] = useState('all');
    const [selectedRole, setSelectedRole] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [selectedTerritory, setSelectedTerritory] = useState(null);
    const [filterSort, setFilterSort] = useState('latest');
    const [isProfilesHidden, setIsProfilesHidden] = useState(false);
    const [isCandidateProfileGridView, setIsCandidateProfileGridView] = useState(false);
    const [isCandidateProfilesSelected, setIsCandidateProfilesSelected] = useState(false);
    const [isCompareActive, setIsCompareActive] = useState(false);

    const [apiProfiles, setApiProfiles] = useState([]);
    const [totalProfilesCount, setTotalProfilesCount] = useState(null);
    // eslint-disable-next-line no-unused-vars
    const [isProfilesLoading, setIsProfilesLoading] = useState(false);
    const isProfilesLoadingRef = useRef(false);
    const [apiFilterOptions, setApiFilterOptions] = useState({
        roles: [],
        companies: [],
        regions: [],
        states: [],
        territories: [],
        territories_by_state: {}
    });

    const [selectedCards, setSelectedCards] = useState([]);
    const [profileSearchQuery, setProfileSearchQuery] = useState('');
    /** Debounced version of profileSearchQuery — used for API calls (min 3 chars, 400ms delay). */
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    useEffect(() => {
        const term = profileSearchQuery.trim();
        if (term.length === 0) {
            // Clear immediately so results reset without waiting for debounce
            setDebouncedSearchQuery('');
            return;
        }
        // Only trigger an API search when the term meets the 3-char minimum
        if (term.length < 3) return;
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(term);
        }, 400);
        return () => clearTimeout(timer);
    }, [profileSearchQuery]);

    const talentPulseWebSearchEnabled = useSelector((state) => state.state.talentPulseWebSearchEnabled);

    const richInputRef = useRef(null);
    const chatEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const streamReaderRef = useRef(null);
    const streamAbortControllerRef = useRef(null);
    const pendingConsentMessageIdRef = useRef(null);
    const prevConversationLengthRef = useRef(0);
    const chatPromptContainerRef = useRef(null);
    const voiceStopWithQueryTimeoutRef = useRef(null);
    const sliderRailRef = useRef(null);
    // Ref to hold loadProfilesFromApi to avoid TDZ issues in syncSliderScrollbar
    const loadProfilesFromApiRef = useRef(null);
    const selectedCandidatesMapRef = useRef({});
    const [sliderScrollbar, setSliderScrollbar] = useState({ visible: false, width: 100, offset: 0 });

    const syncSliderScrollbar = useCallback(() => {
        const rail = sliderRailRef.current;
        if (!rail) return;

        const maxScroll = rail.scrollWidth - rail.clientWidth;
        if (maxScroll <= 1) {
            setSliderScrollbar({ visible: false, width: 100, offset: 0 });
        } else {
            const width = Math.max((rail.clientWidth / rail.scrollWidth) * 100, 12);
            const offset = (rail.scrollLeft / maxScroll) * (100 - width);
            setSliderScrollbar({ visible: true, width, offset });
        }

        // Infinite scroll pagination when scrolling right in slider rail
        // Only fires when user actually scrolls near the end — NOT on initial paint
        if (rail.scrollLeft > 0 && rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 100) {
            if (
                hasMoreProfilesRef.current &&
                !isAnyFetchInProgressRef.current
            ) {
                loadProfilesFromApiRef.current?.(profilesPageRef.current + 1, true);
            }
        }
    }, []);

    /** Mirrors isLoading to avoid stale closure in keyword callback. */
    const isLoadingRef = useRef(false);
    /** Mirrors stopStreaming for cross-declaration access. */
    const handleStopStreamingRef = useRef(null);
    const [showScrollButton, setShowScrollButton] = useState(false);
    const hasChatMessages = uiConversation?.length > 0;
    /** Keep mic active (background keyword detection) while response is generating. */
    const [keepListeningWhileLoading, setKeepListeningWhileLoading] = useState(false);

    const talentPulse360CardsData = {
        talentAcquisition: {
            icon: bulbIcon,
            title: "Talent Acquisition",
            prompts: [
                "Latest interviews and what's the status?",
                "Pipeline candidates currently at Bayer"
            ],
            theme: "purple"
        },
        hiringPipeline: {
            icon: bulbIcon,
            title: "Hiring Pipeline",
            prompts: [
                "TSM candidates interviewed for Maharashtra so far",
                "Candidates with positive panel signals or follow-up flags"
            ],
            theme: "purple"
        },
        territoryPerformance: {
            icon: bulbIcon,
            title: "Territory Performance",
            prompts: [
                "TMs below 70% YTD and competitor TSMs in same territory",
                "Where is UPL losing market share by territory?"
            ],
            theme: "purple"
        }
    };

    useEffect(() => {
        const unsubscribe = subscribe("newTalentPulseChatDirect", () => {
            handleNewSessionContinue();
        });
        return () => {
            unsubscribe();
            textToSpeech.stop();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep isLoadingRef in sync so the keyword handler never reads stale state
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);

    // Direct event listener for voice "stop" keyword — guaranteed to call the
    // latest stopStreaming via handleStopStreamingRef (no stale closure risk).
    useEffect(() => {
        const handleVoiceStop = () => {
            handleStopStreamingRef.current?.();
            textToSpeech.stop();
            window.dispatchEvent(new CustomEvent("azureVopStop", { detail: { forceAll: true } }));
        };
        window.addEventListener("voiceStopKeyword", handleVoiceStop);
        return () => window.removeEventListener("voiceStopKeyword", handleVoiceStop);
    }, []);

    // When voice captures >2 words during TTS playback (background mode),
    // auto-send that text as a new query with voice output enabled.
    useEffect(() => {
        const handleVoiceStopWithQuery = (e) => {
            const query = e.detail?.query;
            if (!query?.trim()) return;
            if (voiceStopWithQueryTimeoutRef.current) {
                clearTimeout(voiceStopWithQueryTimeoutRef.current);
            }
            voiceStopWithQueryTimeoutRef.current = setTimeout(() => {
                handleSendMessage(query, { retainVoiceOutput: true });
            }, 1500);
        };
        window.addEventListener("voiceStopWithQuery", handleVoiceStopWithQuery);
        return () => {
            window.removeEventListener("voiceStopWithQuery", handleVoiceStopWithQuery);
            if (voiceStopWithQueryTimeoutRef.current) {
                clearTimeout(voiceStopWithQueryTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCardCheck = (id, isChecked) => {
        const normalizedId = normalizeCandidateId(id);
        if (!normalizedId) return;

        if (isChecked) {
            if (selectedCards.length >= 5 && !selectedCards.includes(normalizedId)) {
                toast.info("You can select up to 5 candidate profiles for comparison.");
                return;
            }
            const candidateObj = candidatesSource.find((c) => (
                c.id === normalizedId || normalizeCandidateId(c.candidateId) === normalizedId
            ));
            if (candidateObj) {
                selectedCandidatesMapRef.current[normalizedId] = candidateObj;
            }
            setSelectedCards(prev => (
                prev.includes(normalizedId) ? prev : [...prev, normalizedId]
            ));
        } else {
            delete selectedCandidatesMapRef.current[normalizedId];
            setSelectedCards(prev => prev.filter(cardId => cardId !== normalizedId));
        }
    };



    // Fetch filter options (GET /api/profiles/filters)
    // Skipped entirely when CANDIDATE_PROFILES_ENABLED is false
    useEffect(() => {
        if (!CANDIDATE_PROFILES_ENABLED) return;
        const loadFilters = async () => {
            try {
                const res = await dispatch(fetchTalentPulseProfileFilters());
                // Handle: { role_types: [...] } OR { data: { role_types: [...] } } OR { data: { data: { role_types: [...] } } }
                const data = res?.data?.data || res?.data || res;
                if (data && typeof data === 'object' && !data.error) {
                    const roles = data.role_types || data.roles || [];
                    const companies = data.companies || [];
                    const regions = data.regions || [];
                    const states = data.states || [];
                    const territories = data.territories || [];
                    const territories_by_state = data.territories_by_state || {};
                    if (roles.length || companies.length || states.length || territories.length || Object.keys(territories_by_state).length) {
                        setApiFilterOptions({ roles, companies, regions, states, territories, territories_by_state });
                    }
                }
            } catch (err) {
                console.error("Failed to load profile filters from API:", err);
            }
        };
        loadFilters();
    }, [dispatch]);


    // Map API profile schema to component card candidate format
    const mapProfileItemToCandidate = useCallback((item) => {
        const cid = item.candidate_id || item.id || `TP-E000`;
        const nameStr = item.name || "Candidate";
        const initials = nameStr.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
        const company = item.company || item.meta?.current_company || item.stats?.find(s => s.label === 'Company')?.value || '—';
        const status = item.status || item.stats?.find(s => s.label === 'Status')?.value || 'Interviewed';
        const experience = item.meta?.experience
            || item.experience
            || item.total_experience
            || item.total_experience_years
            || item.experience_years
            || item.years_of_experience
            || item.stats?.find(s => s.label === 'Experience')?.value
            || '—';
        const source = item.source || item.stats?.find(s => s.label === 'Source')?.value || 'Interview';
        const role = item.role || item.meta?.current_role || item.headline || 'Candidate Profile';
        const locationStr = item.location || (item.territory && item.state ? `${item.territory}, ${item.state}` : item.territory || item.state || '');

        // Parse previous organisation from career_timeline or cv_info or explicit fields
        const rawTimeline = Array.isArray(item.career_timeline) && item.career_timeline.length > 0
            ? item.career_timeline
            : (Array.isArray(item.career) ? item.career : []);
        const normalizedTimeline = getNormalizedCareerTimeline(rawTimeline, company);

        let prevOrg = item.prev_organization || item.previous_organization || item.previous_org || item.prev_org;
        if (!prevOrg && normalizedTimeline.length > 1) {
            // Pick the second entry (most recent previous company)
            const secondItem = normalizedTimeline[1];
            if (secondItem) {
                const comp = secondItem.company || secondItem.organization || secondItem.org;
                const r = secondItem.role || secondItem.designation || '';
                const ten = secondItem.tenure || secondItem.duration || '';
                prevOrg = [r, comp, ten].filter(Boolean).join(' — ');
            }
        }
        if (!prevOrg && item.cv_info) {
            const cvComp = item.cv_info.company;
            const cvDesig = item.cv_info.designation;
            // Only use cv_info if it doesn't duplicate the top-level current company
            const isSameAsCurrentCompany = cvComp && (
                cvComp.toLowerCase() === (company || '').toLowerCase() ||
                cvComp.toLowerCase() === (item.meta?.current_company || '').toLowerCase()
            );
            if (cvComp && !isSameAsCurrentCompany) {
                prevOrg = cvDesig ? `${cvDesig} — ${cvComp}` : cvComp;
            }
        }
        if (!prevOrg) prevOrg = '—';

        const currentOrgStr = (item.cv_info?.company && item.cv_info?.designation)
            ? `${item.cv_info.designation} — ${item.cv_info.company}`
            : `${item.meta?.current_role || item.role_type || ''} — ${company}`.replace(/^—\s*|\s*—$/, '');

        return {
            id: normalizeCandidateId(cid),
            initials,
            name: nameStr,
            candidateId: cid,
            type: item.type || 'external',
            roleType: item.role_type || '',
            company,
            region: item.region || '',
            state: item.state || '',
            territory: item.territory || '',
            status,
            experience,
            source,
            details: [
                { text: role },
                { icon: "📍", text: locationStr || 'Location not available' }
            ],
            profile: {
                role,
                location: locationStr || 'Location not available',
                currentOrg: currentOrgStr || '—',
                prevOrg: prevOrg,
                target: item.headline || item.meta?.experience || '—',
                interviewDate: item.interview_date || item.meta?.date || '—',
                current: `${company} · (current org)`,
                insights: Array.isArray(item.insights) ? item.insights : (Array.isArray(item.upl_intel) ? item.upl_intel : []),
                strategy: Array.isArray(item.strategy) ? item.strategy : (Array.isArray(item.communication) ? item.communication : []),
                quote: item.quote || '',
                nextStep: item.next_step || 'Panel feedback',
                career: normalizedTimeline,
                career_timeline: normalizedTimeline,
                compensation: item.compensation || {},
                cvInfo: item.cv_info || null
            }
        };
    }, []);

    const [profilesPage, setProfilesPage] = useState(1);
    const [hasMoreProfiles, setHasMoreProfiles] = useState(false);
    const [isFetchingNextProfiles, setIsFetchingNextProfiles] = useState(false);

    const isFetchingNextProfilesRef = useRef(false);
    const hasMoreProfilesRef = useRef(false);
    const profilesPageRef = useRef(1);
    /** Single unified lock: true whenever ANY profile API call is in-flight (including a brief post-fetch cooldown). */
    const isAnyFetchInProgressRef = useRef(false);

    useEffect(() => {
        isFetchingNextProfilesRef.current = isFetchingNextProfiles;
    }, [isFetchingNextProfiles]);

    useEffect(() => {
        hasMoreProfilesRef.current = hasMoreProfiles;
    }, [hasMoreProfiles]);

    useEffect(() => {
        profilesPageRef.current = profilesPage;
    }, [profilesPage]);

    // Fetch profile list (GET /api/profiles) with pagination
    // Skipped entirely when CANDIDATE_PROFILES_ENABLED is false
    const loadProfilesFromApi = useCallback(async (pageNum = 1, isAppend = false) => {
        if (!CANDIDATE_PROFILES_ENABLED) return;
        // Lock immediately — prevents any concurrent or rapid-fire second call
        isAnyFetchInProgressRef.current = true;
        if (isAppend) {
            setIsFetchingNextProfiles(true);
            isFetchingNextProfilesRef.current = true;
        } else {
            setIsProfilesLoading(true);
            isProfilesLoadingRef.current = true;
        }
        try {
            const queryParams = {
                page: pageNum,
                page_size: 20
            };
            // Only send type=external if the external profiles feature is enabled
            if (filterType && filterType !== 'all') queryParams.type = filterType;
            if (selectedRole) queryParams.role_type = selectedRole;
            if (selectedCompany) queryParams.company = selectedCompany;
            if (selectedRegion) queryParams.region = selectedRegion;
            if (selectedState) queryParams.state = selectedState;
            if (selectedTerritory) queryParams.territory = selectedTerritory;
            // Search: minimum 3 characters required by API
            if (debouncedSearchQuery.length >= 3) queryParams.search = debouncedSearchQuery;
            if (filterSort === 'az') {
                queryParams.sort_by = 'name';
                queryParams.order = 'asc';
            }

            const res = await dispatch(fetchTalentPulseProfiles(queryParams));
            const rawData = res?.data?.data || res?.data || res;
            const items = rawData?.data || rawData?.items || rawData?.profiles || (Array.isArray(rawData) ? rawData : null);

            if (Array.isArray(items)) {
                const mapped = items.map(mapProfileItemToCandidate);
                if (isAppend) {
                    setApiProfiles(prev => {
                        const existingIds = new Set(prev.map(p => p.candidateId || p.id));
                        const uniqueNew = mapped.filter(p => !existingIds.has(p.candidateId || p.id));
                        return [...prev, ...uniqueNew];
                    });
                } else {
                    setApiProfiles(mapped);
                }

                const totalVal = rawData?.total ?? res?.data?.total ?? res?.total;
                if (typeof totalVal === 'number') {
                    setTotalProfilesCount(totalVal);
                } else {
                    setTotalProfilesCount(null);
                }

                let hasMore = false;
                const PAGE_SIZE = 20;
                if (typeof rawData?.has_more === 'boolean') {
                    hasMore = rawData.has_more;
                } else if (typeof totalVal === 'number') {
                    const loadedCount = (pageNum - 1) * PAGE_SIZE + items.length;
                    hasMore = loadedCount < totalVal;
                } else {
                    // If we got fewer items than page_size, there's no next page
                    hasMore = items.length >= PAGE_SIZE;
                }

                setHasMoreProfiles(hasMore);
                setProfilesPage(pageNum);
            }
        } catch (err) {
            console.error("Failed to load profiles from API:", err);
        } finally {
            setIsProfilesLoading(false);
            isProfilesLoadingRef.current = false;
            setIsFetchingNextProfiles(false);
            isFetchingNextProfilesRef.current = false;
            // Keep the unified lock on for a short cooldown so the RAF that fires
            // after visibleCandidates re-renders doesn't immediately trigger the next page.
            setTimeout(() => {
                isAnyFetchInProgressRef.current = false;
            }, 300);
        }
    }, [dispatch, filterType, selectedRole, selectedCompany, selectedRegion, selectedState, selectedTerritory, filterSort, debouncedSearchQuery, mapProfileItemToCandidate]);

    // Keep the ref up to date so syncSliderScrollbar can call it without TDZ issues
    useEffect(() => {
        loadProfilesFromApiRef.current = loadProfilesFromApi;
    });

    useEffect(() => {
        setProfilesPage(1);
        loadProfilesFromApi(1, false);
    }, [loadProfilesFromApi]);

    // Fetch individual profile detail (GET /api/profiles/{id}) on demand
    const handleFetchProfileDetail = useCallback(async (targetId) => {
        if (!targetId) return;
        try {
            const res = await dispatch(fetchTalentPulseProfileDetail(targetId));
            const detailData = res?.data?.data || res?.data || res;
            if (detailData && (detailData.candidate_id || detailData.id)) {
                const mappedDetail = mapProfileItemToCandidate(detailData);
                setApiProfiles(prev => prev.map(p => (
                    p.candidateId === targetId || p.id === targetId || p.candidateId === detailData.candidate_id || p.id === detailData.id
                ) ? { ...p, ...mappedDetail } : p));
                return mappedDetail;
            }
        } catch (err) {
            console.error(`Failed to fetch detail for candidate ${targetId}:`, err);
        }
    }, [dispatch, mapProfileItemToCandidate]);

    useEffect(() => {
        if (apiProfiles.length > 0) {
            apiProfiles.forEach((candidate) => {
                const normId = normalizeCandidateId(candidate.candidateId || candidate.id);
                if (normId && selectedCards.includes(normId)) {
                    selectedCandidatesMapRef.current[normId] = candidate;
                }
            });
        }
    }, [apiProfiles, selectedCards]);

    const candidatesSource = apiProfiles;

    const selectedCandidates = selectedCards
        .map((cardId) => {
            const normId = normalizeCandidateId(cardId);
            return (
                candidatesSource.find((candidate) => (
                    candidate.id === normId
                    || normalizeCandidateId(candidate.candidateId) === normId
                )) || selectedCandidatesMapRef.current[normId]
            );
        })
        .filter(Boolean);

    const selectedCandidateNames = selectedCandidates.map((candidate) => candidate.name);

    const roles = apiFilterOptions.roles.length > 0
        ? apiFilterOptions.roles
        : [...new Set(candidatesSource.map(getCandidateRole))];
    const companies = apiFilterOptions.companies.length > 0
        ? apiFilterOptions.companies
        : [...new Set(candidatesSource.map((candidate) => candidate.company))];
    const regions = apiFilterOptions.regions.length > 0
        ? apiFilterOptions.regions
        : [...new Set(candidatesSource.map(getCandidateRegion))];
    const states = apiFilterOptions.states.length > 0
        ? apiFilterOptions.states
        : [...new Set(candidatesSource.map(getCandidateState))];
    const availableTerritoriesForState = (selectedState && apiFilterOptions.territories_by_state?.[selectedState])
        ? apiFilterOptions.territories_by_state[selectedState]
        : apiFilterOptions.territories;

    const territories = availableTerritoriesForState.length > 0
        ? availableTerritoriesForState
        : [...new Set(candidatesSource.map(getCandidateTerritory))];

    const filteredCandidates = candidatesSource
        .filter((candidate) => filterType !== 'external' || candidate.company !== 'UPL')
        .sort((first, second) => filterSort === 'az' ? first.name.localeCompare(second.name) : 0);

    // Search: API handles >= 3 chars (via debouncedSearchQuery).
    // Client-side search is only a fallback for 1-2 char terms while debounce is pending.
    const searchLower = profileSearchQuery.trim().toLowerCase();
    const isApiHandlingSearch = debouncedSearchQuery.length >= 3;
    const visibleCandidates = (!isApiHandlingSearch && searchLower)
        ? filteredCandidates.filter((c) =>
            (c.name || '').toLowerCase().includes(searchLower) ||
            (c.candidateId || '').toLowerCase().includes(searchLower) ||
            (c.company || '').toLowerCase().includes(searchLower) ||
            (c.details?.[0]?.text || '').toLowerCase().includes(searchLower)
        )
        : filteredCandidates;

    useEffect(() => {
        const frame = requestAnimationFrame(syncSliderScrollbar);
        return () => cancelAnimationFrame(frame);
    }, [visibleCandidates.length, isCandidateProfilesSelected, isProfilesHidden, syncSliderScrollbar]);

    const handleStateChange = useCallback((newState) => {
        setSelectedState(newState);
        if (selectedTerritory) {
            const validTerritories = (newState && apiFilterOptions.territories_by_state?.[newState])
                ? apiFilterOptions.territories_by_state[newState]
                : apiFilterOptions.territories;
            if (validTerritories.length > 0 && !validTerritories.includes(selectedTerritory)) {
                setSelectedTerritory(null);
            }
        }
    }, [selectedTerritory, apiFilterOptions.territories_by_state, apiFilterOptions.territories]);

    const resetCandidateFilters = useCallback(() => {
        selectedCandidatesMapRef.current = {};
        setIsCandidateProfilesSelected(false);
        setSelectedCards([]);
        setFilterType('all');
        setSelectedRole(null);
        setSelectedCompany(null);
        setSelectedRegion(null);
        setSelectedState(null);
        setSelectedTerritory(null);
        setFilterSort('latest');
        setIsCandidateProfileGridView(false);
        setIsProfilesHidden(false);
        setIsCompareActive(false);
        setProfileSearchQuery('');
    }, []);

    const handleCompareReset = useCallback(() => {
        selectedCandidatesMapRef.current = {};
        setIsCompareActive(false);
        setSelectedCards([]);
        setIsProfilesHidden(false);
        setFilterType('all');
        setSelectedRole(null);
        setSelectedCompany(null);
        setSelectedRegion(null);
        setSelectedState(null);
        setSelectedTerritory(null);
        setFilterSort('latest');
        setProfileSearchQuery('');
    }, []);

    const withUnhide = useCallback((setter) => (val) => {
        setter(val);
        setIsProfilesHidden(false);
    }, []);

    const handleResetAllFilters = () => {
        resetCandidateFilters();
        handleNewSessionContinue();
    };

    // Hide the + (Profiles chip) entirely when the candidate profiles feature is disabled
    const contentPulseChipConfig = CANDIDATE_PROFILES_ENABLED ? {
        heading: "PROFILES",
        context: "Candidate Profiles",
        isActive: isCandidateProfilesSelected,
        onSelect: async () => {
            await handleNewSessionContinue();
            setIsCandidateProfilesSelected(true);
            setIsCandidateProfileGridView(false);
            setIsProfilesHidden(false);
        },
        onRemove: handleResetAllFilters
    } : undefined;

    const removeSelectedCandidate = (candidateId) => {
        const normId = normalizeCandidateId(candidateId);
        delete selectedCandidatesMapRef.current[normId];
        setSelectedCards((prev) => prev.filter((id) => id !== normId));
    };

    const maxNamesToShow = isMobile ? 1 : 2;
    const selectedCandidateChipConfig = selectedCandidateNames.length ? {
        label: `${selectedCandidateNames.length} selected: ${selectedCandidateNames.slice(0, maxNamesToShow).join(", ")}${selectedCandidateNames.length > maxNamesToShow ? "..." : ""}`,
        candidates: selectedCandidates,
        onRemove: () => handleCompareReset(),
        onRemoveCandidate: (candidateId) => removeSelectedCandidate(candidateId)
    } : undefined;

    const profileTypeChipConfig = filterType === 'external' ? {
        label: 'Type: External',
        onRemove: () => setFilterType('all')
    } : undefined;

    const handleInputChange = (text) => {
        setMessage(text);
    };

    const handlePromptClick = (prompt) => {
        setMessage(prompt);
        setTimeout(() => {
            richInputRef.current?.focus?.();
        }, 50);
    };

    const ensureSessionId = () => {
        const existingSessionId = sessionManager.getSessionId("talentPulse360SessionId");
        if (existingSessionId && existingSessionId !== "undefined") return existingSessionId;
        try {
            const newSessionId = generateSessionId();
            sessionManager.setSessionId(newSessionId, "talentPulse360SessionId");
            return newSessionId;
        } catch (error) {
            console.error("Error generating Talent Pulse session ID:", error);
            return "";
        }
    };

    const handleAbortMessageError = (messageId) => {
        setUIConversation((prev) => {
            const newConvo = [...prev];
            const lastIndex = newConvo.length - 1;
            if (newConvo[lastIndex]?.id === messageId) {
                newConvo[lastIndex] = {
                    ...newConvo[lastIndex],
                    isPlaceholder: false,
                    content: "Sorry, I'm having trouble right now.",
                };
            }
            return newConvo;
        });
        streamReaderRef.current = null;
        setIsLoading(false);
    };

    const updateAssistantMessage = (messageId, updates) => {
        setUIConversation((prev) => {
            const newConvo = [...prev];
            const lastIndex = newConvo.length - 1;
            if (newConvo[lastIndex]?.id === messageId) {
                newConvo[lastIndex] = { ...newConvo[lastIndex], ...updates };
            }
            return newConvo;
        });
    };

    const handleSendMessage = async (...args) => {
        let customContent = null;
        let enableWebSearch = false;
        let skipConsent = false;
        let retainVoiceOutput = false;
        let webConsent = null;
        let isCompareRequest = false;

        const firstArg = args[0];
        const secondArg = args[1];

        if (typeof firstArg === "string") {
            customContent = firstArg;
            if (typeof secondArg === "object" && secondArg !== null) {
                enableWebSearch = secondArg.enableWebSearch ?? false;
                skipConsent = secondArg.skipConsent ?? false;
                retainVoiceOutput = secondArg.retainVoiceOutput === true;
                webConsent = secondArg.webConsent ?? null;
                isCompareRequest = secondArg.isCompareRequest ?? false;
            } else {
                // Positional arguments: (customContent, enableWebSearch, skipConsent, retainVoiceOutput)
                enableWebSearch = Boolean(args[1]);
                skipConsent = Boolean(args[2]);
                retainVoiceOutput = args[3] === true;
            }
        } else if (typeof firstArg === "object" && firstArg !== null) {
            customContent = firstArg.customContent || firstArg.content || null;
            enableWebSearch = firstArg.enableWebSearch ?? false;
            skipConsent = firstArg.skipConsent ?? false;
            retainVoiceOutput = firstArg.retainVoiceOutput === true;
            webConsent = firstArg.webConsent ?? null;
            isCompareRequest = firstArg.isCompareRequest ?? false;
        }

        const effectiveSkipConsent = skipConsent;
        const effectiveWebConsent = webConsent;
        const content = (typeof customContent === "string" ? customContent : message)?.trim();
        if (!content || isLoading) return;

        const messageId = generateCustomUUID();
        const timestamp = new Date().toISOString();
        // VOP card is disabled when external candidate profiles are selected
        const wasVoiceInput = isCandidateProfilesSelected ? false : (retainVoiceOutput || alexaActiveRef.current);

        // If voice-triggered, keep mic open during streaming and enable auto voice playback
        if (wasVoiceInput) {
            alexaActiveRef.current = true;
            setAutoPlayLastMessage(true);
            autoPlayLastMessageRef.current = true;
            setKeepListeningWhileLoading(true);
            setTimeout(() => {
                chatPromptContainerRef.current?.startListening?.();
            }, 300);
        } else {
            alexaActiveRef.current = false;
            setAutoPlayLastMessage(false);
            autoPlayLastMessageRef.current = false;
        }

        setUIConversation((prev) => [
            ...prev,
            { role: "user", content, id: `${messageId}-user`, messageId, timestamp },
            { role: "assistant", content: "Loading...", isPlaceholder: true, id: messageId, messageId, feedback: "", timestamp, steps: [], enableVoiceOutput: !isCandidateProfilesSelected, autoPlayVoice: wasVoiceInput },
        ]);
        setMessage("");
        richInputRef.current?.clear?.();
        setIsLoading(true);

        if (isCandidateProfilesSelected) {
            setIsProfilesHidden(true);
            setIsCandidateProfileGridView(false);
        }

        const candidatesList = selectedCandidates.map((candidate) => ({
            candidate_id: candidate.candidateId || candidate.id || "",
            name: candidate.name || "",
        }));

        const filtersObj = {
            ...(filterType && filterType !== "all" ? { filter_type: filterType } : {}),
            ...(selectedRole ? { role: selectedRole } : {}),
            ...(selectedCompany ? { company: selectedCompany } : {}),
            ...(selectedRegion ? { region: selectedRegion } : {}),
            ...(selectedState ? { state: selectedState } : {}),
            ...(selectedTerritory ? { territory: selectedTerritory } : {}),
        };

        const profileContext = {
            candidates: candidatesList,
            filters: filtersObj,
            is_compare: isCompareRequest,
        };

        const currentSessionId = ensureSessionId();
        const payload = {
            session_id: currentSessionId,
            query: content,
            message_id: messageId,
            enable_web_search: talentPulseWebSearchEnabled || enableWebSearch,
            is_voice_used: wasVoiceInput,
            profile_context: profileContext,
            ...(effectiveWebConsent ? { web_consent: effectiveWebConsent } : {}),
        };

        try {
            const abortController = new AbortController();
            streamAbortControllerRef.current = abortController;
            const response = await fetchTalentPulseCompletions(payload, abortController);

            if (response?.status === 401) {
                handleUnauthorized(navigate, undefined, undefined, constants.SESSION_EXPIRED);
                return;
            }
            if (!response.ok || !response.body) throw new Error("Streaming failed");

            const reader = response.body.getReader();
            streamReaderRef.current = reader;
            const decoder = new TextDecoder("utf-8");

            let localSteps = [];
            let messageCitations = [];
            let localCharts = {};
            let finalResponseData = null;
            let isStreamComplete = false;
            let leftover = "";

            const processSseLine = (line) => {
                const trimmed = line.trim();
                let rawStr = trimmed;
                if (trimmed.startsWith("data:")) {
                    rawStr = trimmed.slice(5).trim();
                }
                if (!rawStr || rawStr === "[DONE]") return;
                let dataStr = rawStr;
                try {
                    const decrypted = encrypted.decrypt(rawStr);
                    if (decrypted) dataStr = decrypted;
                } catch (e) { }

                try {
                    const eventObj = JSON.parse(dataStr);
                    if (
                        eventObj?.type === "web_search_consent_required" ||
                        eventObj?.type === SSE_EVENTS.WEB_SEARCH_CONSENT_REQUIRED ||
                        (eventObj?.type === SSE_EVENTS.WEB_SEARCH_SUGGESTION && eventObj?.data?.reason === SSE_EVENTS.ROUTER_DISABLED)
                    ) {
                        if (!effectiveSkipConsent) {
                            setShowWebConsent(true);
                            pendingConsentMessageIdRef.current = messageId;
                        }
                    }
                    if (eventObj?.type === SSE_EVENTS.ERROR) {
                        throw new Error(eventObj?.data?.message || "Streaming error");
                    }
                    if (eventObj?.type === SSE_EVENTS.CHART || eventObj?.type === "chart") {
                        const graphJson = eventObj?.data?.graph_json;
                        const chartKey = eventObj?.data?.chart_key || 'default';
                        if (graphJson) {
                            try {
                                const parsedGraph = typeof graphJson === "string" ? JSON.parse(graphJson) : graphJson;
                                localCharts[chartKey] = parsedGraph;
                                updateAssistantMessage(messageId, { charts: { ...localCharts } });
                            } catch (e) { }
                        }
                    }

                    const res = handleTalentPulseStreamEvent(eventObj, localSteps, messageCitations, currentSessionId);
                    localSteps = res.steps;
                    messageCitations = res.citations;
                    if (res.isComplete) isStreamComplete = true;
                    if (res.finalResponseData) finalResponseData = res.finalResponseData;

                    const finalStep = localSteps.find((s) => s.type === "final_response");
                    updateAssistantMessage(messageId, {
                        isPlaceholder: !finalStep,
                        content: finalStep ? finalStep.content : "",
                        steps: [...localSteps],
                        citations: [...messageCitations],
                        charts: { ...localCharts },
                    });
                } catch (err) {
                    /* skip malformed lines */
                }
            };

            while (true) {
                const { value, done } = await reader.read();
                const chunk = decoder.decode(value || new Uint8Array(), { stream: !done });
                const combined = leftover + chunk;
                const lines = combined.split("\n");
                leftover = lines.pop() || "";
                if (done && leftover) { lines.push(leftover); leftover = ""; }

                lines.forEach(processSseLine);
                if (done || isStreamComplete) break;
            }

            const finalStep = localSteps.find((s) => s.type === "final_response");
            const finalResponseText = finalStep?.content || finalResponseData?.response || finalResponseData?.text || finalResponseData?.content || "";

            updateAssistantMessage(messageId, {
                isPlaceholder: false,
                content: finalResponseText || "Sorry, I couldn't generate a response. Please try again.",
                steps: [...localSteps],
                citations: [...messageCitations],
                spokenDuration: computeSpokenDuration(finalResponseText || ""),
                charts: { ...localCharts },
            });

            if (uiConversation.length < 3 || finalResponseData?.session_title || finalResponseData?.thread_title) {
                emit("updateThreads");
            }
        } catch (error) {
            if (error.name === "AbortError") return;
            console.error("Talent Pulse chat error:", error);
            handleAbortMessageError(messageId);
        } finally {
            streamReaderRef.current = null;
            streamAbortControllerRef.current = null;
            setIsLoading(false);
            // Mic lifecycle during/after streaming is managed by useAlexaMode hook
        }
    };

    const handleCompareProfiles = () => {
        if (selectedCards.length < 2) return;
        setIsCompareActive(true);
        const names = selectedCandidateNames.join(", ");
        handleSendMessage(`Compare candidate profiles: ${names}`, { isCompareRequest: true });
    };

    const stopStreaming = (explicitMessageId = null) => {
        textToSpeech.stop();
        const hadActiveStream =
            !!streamAbortControllerRef.current ||
            !!streamReaderRef.current ||
            isLoading;

        if (streamAbortControllerRef.current) {
            try {
                streamAbortControllerRef.current.abort();
            } catch (err) {
                console.warn("AbortController error:", err);
            }
        }

        if (streamReaderRef.current) {
            try {
                const cancelPromise = streamReaderRef.current.cancel();
                if (cancelPromise && cancelPromise.catch) {
                    cancelPromise.catch((err) => {
                        if (err.name !== "AbortError") {
                            console.warn("Stream reader cancel error:", err);
                        }
                    });
                }
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.warn("Stream reader cancel error:", err);
                }
            }
            streamReaderRef.current = null;
        }

        let targetMessageId = explicitMessageId;
        if (!targetMessageId) {
            const lastMsg = uiConversation[uiConversation.length - 1];
            if (lastMsg?.isPlaceholder) {
                targetMessageId = lastMsg.messageId;
            }
        }

        if (targetMessageId) {
            dispatch(stopTalentPulseChatStream({ message_id: targetMessageId }));
        }

        setIsLoading(false);
        setUIConversation((prev) => {
            const newConvo = [...prev];
            const lastIndex = newConvo.length - 1;
            if (lastIndex >= 0 && (newConvo[lastIndex]?.isPlaceholder || newConvo[lastIndex]?.role === "assistant")) {
                const currentMsg = newConvo[lastIndex];
                const updatedSteps = Array.isArray(currentMsg.steps)
                    ? currentMsg.steps.map((s) => (s.status === "processing" ? { ...s, status: "done" } : s))
                    : currentMsg.steps;

                newConvo[lastIndex] = {
                    ...currentMsg,
                    isPlaceholder: false,
                    steps: updatedSteps,
                    content:
                        currentMsg.content === "Loading..." || !currentMsg.content?.trim()
                            ? "Streaming Stopped"
                            : currentMsg.content,
                };
            }
            return newConvo;
        });

        if (hadActiveStream && uiConversation.length < 3) {
            setTimeout(() => {
                emit("updateThreads");
            }, 2000);
        }
    };

    // Keep ref current so handleVoiceKeyword can always call the latest stopStreaming
    handleStopStreamingRef.current = stopStreaming;

    // ── Alexa-mode voice session ─────────────────────────────────────────────
    const alexa = useAlexaMode({
        message,
        isLoading,
        handleSendMessage,
        stopStreaming,
        chatPromptContainerRef,
        setKeepListening: setKeepListeningWhileLoading,
        alexaActiveRef,
    });

    const handleVoiceStart = useCallback(() => {
        alexaActiveRef.current = true;
        alexa.onVoiceStart();
    }, [alexa]);

    const handleVoiceStop = useCallback(() => {
        alexaActiveRef.current = false;
        setAutoPlayLastMessage(false);
        autoPlayLastMessageRef.current = false;
        alexa.onVoiceStop();
    }, [alexa]);

    const handleVoiceKeyword = useCallback(() => {
        setAutoPlayLastMessage(false);
        autoPlayLastMessageRef.current = false;
        alexa.onVoiceKeyword();
    }, [alexa]);

    const handleEditClick = (index, pair) => {
        if (pair?.role === "user") {
            setEditingId(index);
            setEditedText(uiConversation[index]?.content || "");
        }
    };

    const handleSave = (editIndex) => {
        setEditingId(null);
        if (!editedText.trim()) return;
        setUIConversation((prev) =>
            prev.map((msg, idx) =>
                idx === editIndex ? { ...msg, content: editedText } : msg
            )
        );
        setEditedText("");
    };

    useEffect(() => {
        if (uiConversation.length > prevConversationLengthRef.current) {
            scrollToLatestViewPoint(chatEndRef, isMobile, true);
        }
        prevConversationLengthRef.current = uiConversation.length;
    }, [uiConversation, isMobile]);

    const stopAllVoiceAndStreaming = () => {
        // Reset the Alexa voice session first — clears alexaActiveRef, idle timers,
        // closes STT WebSocket, and kills TTS / Azure VOP / Streaming VOP.
        alexa.onVoiceStop();
        stopStreaming();
        setKeepListeningWhileLoading(false);
        setAutoPlayLastMessage(false);
        autoPlayLastMessageRef.current = false;
        alexaActiveRef.current = false;
        // Use both stopVoice (resets UI + sound) and stopListening (closes WS)
        chatPromptContainerRef.current?.stopVoice?.();
        chatPromptContainerRef.current?.stopListening?.();
        if (voiceStopWithQueryTimeoutRef.current) {
            clearTimeout(voiceStopWithQueryTimeoutRef.current);
            voiceStopWithQueryTimeoutRef.current = null;
        }
        textToSpeech.stop();
        window.dispatchEvent(new CustomEvent("azureVopStop", { detail: { forceAll: true } }));
        window.dispatchEvent(new CustomEvent("streamingVopStop", { detail: { forceAll: true } }));
    };

    const clearChatData = () => {
        stopAllVoiceAndStreaming();
        setUIConversation([]);
        setIsChatLoading(false);
        setAutoPlayLastMessage(false);
        autoPlayLastMessageRef.current = false;
        alexaActiveRef.current = false;
        sessionManager.setSessionId(null, "talentPulse360SessionId");
        setMessage("");
        if (richInputRef.current) {
            richInputRef.current.clear();
        }
    };

    const handleNewSessionContinue = async () => {
        if (location?.pathname !== ROUTES.TALENT_PULSE_360) {
            navigate(ROUTES.TALENT_PULSE_360);
        }
        stopAllVoiceAndStreaming();
        await emit("resetSelectedThread", true);
        clearChatData();
        resetCandidateFilters();

        dispatch(setTalentPulseWebSearchEnabled(false));
        setIsLoading(false);
        setIsChatLoading(false);
    };

    const handleFeedback = async (index, type, data, comment = "") => {
        setUIConversation((prev) =>
            prev.map((msg, idx) =>
                idx === index ? { ...msg, feedback: type } : msg
            )
        );

        if (comment) {
            setFeedbackInput((prev) => ({
                ...prev,
                [index]: { comment },
            }));
        }

        await handleFeedbackSubmit(type, index, data, comment);
    };

    const handleFeedbackSubmit = async (type, index, pair, commentParam = "") => {
        if (submittingFeedback) return;

        setSubmittingFeedback(true);

        try {
            const currentSessionId = ensureSessionId();
            const comment = commentParam !== "" ? commentParam : (feedbackInput?.[index]?.comment?.trim() || "");

            const requestBody = {
                session_id: currentSessionId,
                message_id: pair?.messageId,
                feedback: type ?? "",
                feedback_comment: comment,
                user_id: username,
            };

            await dispatch(updateTalentPulseFeedback(requestBody));

            toast.info(constants.chat?.FEEDBACK_SUBMIT_SUCCESS || "Feedback submitted successfully");

            setFeedbackInput((prev) => ({
                ...prev,
                [index]: { comment: "" },
            }));
        } catch (error) {
            console.error("Talent Pulse feedback submit error:", error);
            toast.error(constants.chat?.FEEDBACK_SUBMIT_FAILED || "Failed to submit feedback");
        } finally {
            setSubmittingFeedback(false);
        }
    };

    const fetchUserConversation = async (user_id, session_id) => {
        setIsChatLoading(true);
        stopAllVoiceAndStreaming();
        setUIConversation([]);
        if (!user_id || !session_id) {
            setIsChatLoading(false);
            handleNewSessionContinue();
            return;
        }
        try {
            const params = { session_id };
            const response = await dispatch(fetchTalentPulseUserConversation(params));

            if (response?.error) {
                console.error("Error fetching Talent Pulse user conversation:", response.error);
                return;
            }

            const rawMessages = Array.isArray(response?.data)
                ? response.data
                : (Array.isArray(response?.data?.data) ? response.data.data : []);

            const profileContext = response?.profile_context || response?.data?.profile_context;
            if (profileContext) {
                const candidates = profileContext.candidates || [];
                const filters = profileContext.filters || {};

                const candidateIds = candidates
                    .map((c) => normalizeCandidateId(c.candidate_id || c.id || c.candidateId))
                    .filter(Boolean);
                if (candidateIds.length > 0) {
                    candidates.forEach((c) => {
                        const normId = normalizeCandidateId(c.candidate_id || c.id || c.candidateId);
                        if (normId) {
                            selectedCandidatesMapRef.current[normId] = mapProfileItemToCandidate(c);
                        }
                    });
                    setSelectedCards(candidateIds.slice(0, 5));
                    setIsCandidateProfilesSelected(true);
                } else if (Object.keys(filters).length > 0 || profileContext.is_compare) {
                    setIsCandidateProfilesSelected(true);
                }

                if (profileContext.is_compare || candidateIds.length > 0) {
                    setIsCompareActive(true);
                } else {
                    setIsCompareActive(false);
                }

                setFilterType(filters.filter_type || "all");
                setSelectedRole(filters.role || null);
                setSelectedCompany(filters.company || null);
                setSelectedRegion(filters.region || null);
                setSelectedState(filters.state || null);
                setSelectedTerritory(filters.territory || null);
            }

            const mappedMessages = rawMessages.map((msg) => {
                const messageId = msg.id || generateCustomUUID();
                const role = msg.role || "user";

                let timestamp = msg.timestamp;
                if (typeof timestamp === "number") {
                    const ms = timestamp < 9999999999 ? timestamp * 1000 : timestamp;
                    timestamp = new Date(ms).toISOString();
                } else if (!timestamp) {
                    timestamp = new Date().toISOString();
                }

                const finalStep = Array.isArray(msg.steps)
                    ? msg.steps.find((s) => s.type === "final_response" || s.label?.toLowerCase() === "assistant" || s.label?.toLowerCase() === "final response" || s.tool === "assistant") ||
                    msg.steps[msg.steps.length - 1]
                    : null;
                const finalStepContent = finalStep?.content || "";
                const rawMsgContent = msg.content || finalStepContent || "";
                const { content: formattedContent, citations: formattedCitations } = formatTalentPulseContent(rawMsgContent, session_id);

                // Also collect citations from steps (tool_result entries) — the API sometimes
                // stores citations only in steps[].citations, not in content[tool].citations.
                // NOTE: deep-clone all step data to avoid mutating Redux state objects.
                const mutableCitations = formattedCitations.map((g) => ({
                    ...g,
                    sources: Array.isArray(g.sources) ? [...g.sources] : [],
                }));

                if (Array.isArray(msg.steps)) {
                    msg.steps.forEach((step) => {
                        const stepSources = (Array.isArray(step.sources) && step.sources.length > 0 ? step.sources : step.citations) || [];
                        if (
                            step.type === "tool_result" &&
                            step.tool &&
                            Array.isArray(stepSources) &&
                            stepSources.length > 0
                        ) {
                            const toolName = step.tool.toLowerCase();
                            const stepCitations = stepSources.map((c) => ({
                                ...c,
                                session_id: c.session_id || c.sessionId || session_id,
                                sessionId: c.sessionId || c.session_id || session_id,
                            })); // clone each citation and preserve sessionId
                            const existing = mutableCitations.find(
                                (g) => g.toolName && g.toolName.toLowerCase() === toolName
                            );
                            if (existing) {
                                const existingTitles = new Set(existing.sources.map((s) => s.title || s.file_id || s.url));
                                stepCitations.forEach((c) => {
                                    const key = c.title || c.file_id || c.url;
                                    if (!existingTitles.has(key)) {
                                        existing.sources = [...existing.sources, c]; // immutable push
                                        existingTitles.add(key);
                                    }
                                });
                            } else {
                                mutableCitations.push({
                                    toolName: step.tool,
                                    sources: stepCitations, // already cloned
                                    sessionId: session_id,
                                });
                            }
                        }
                    });
                }

                let msgSteps = Array.isArray(msg.steps) ? JSON.parse(JSON.stringify(msg.steps)) : null;
                if (msg.role === "assistant" && (msg.charts || msg.graph_json || msg.graph || msg.graphs)) {
                    if (!msgSteps) msgSteps = [];
                    const hasChartStep = msgSteps.some((s) => (s.tool || "").toLowerCase() === "render_chart" || (s.tool || "").toLowerCase() === "chart");
                    if (!hasChartStep) {
                        msgSteps.unshift({
                            id: `step-chart-${messageId}`,
                            tool: "render_chart",
                            label: "Chart",
                            type: "tool_result",
                            status: "done",
                            content: "",
                            citations: [],
                        });
                    }
                }

                const mappedMsg = {
                    role,
                    content: formattedContent || finalStepContent || "",
                    rawContent: typeof msg.content === "object" && msg.content !== null
                        ? JSON.parse(JSON.stringify(msg.content)) // deep clone to avoid Redux mutation
                        : (msg.content || finalStepContent),
                    citations: mutableCitations,

                    id: `${messageId}-${role}`,
                    messageId,
                    sessionId: session_id,
                    session_id: session_id,
                    feedback: msg.feedback || "",
                    timestamp,
                    is_web_search: !!(msg.is_web_search ?? msg.web_search),
                    // Always show VOP card; history messages never autoplay
                    enableVoiceOutput: true,
                    autoPlayVoice: false,
                    steps: msgSteps,
                };

                // Comprehensive Chart Harvester for History Replay
                const parsedCharts = {};
                const contentStr = (typeof msg.content === 'string' ? msg.content : '') + ' ' + (finalStepContent || '');
                const placeholderMatches = [...contentStr.matchAll(/\{\{(CHART_[A-Za-z0-9_]+)\}\}/g)];
                const foundPlaceholderKeys = placeholderMatches.map(m => m[1]);

                const collectedGraphs = [];

                const tryExtractGraph = (item, defaultKey = null) => {
                    if (!item) return;
                    let target = item;
                    if (typeof item === 'object') {
                        if (item.graph_json) target = item.graph_json;
                        else if (item.graph) target = item.graph;
                        else if (item.data?.graph_json) target = item.data.graph_json;
                        else if (item.data?.graph) target = item.data.graph;
                    }
                    if (!target) return;

                    let parsed = null;
                    if (typeof target === 'string') {
                        const trimmed = target.trim();
                        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
                            try { parsed = JSON.parse(trimmed); } catch (e) { }
                        }
                    } else if (typeof target === 'object' && target !== null) {
                        parsed = target;
                    }

                    if (parsed && (parsed.data || parsed.layout || Array.isArray(parsed))) {
                        const key = item?.chart_key || item?.data?.chart_key || parsed?.chart_key || defaultKey;
                        collectedGraphs.push({ key, graph: parsed });
                    }
                };

                // 1. Check msg.charts
                if (msg.charts) {
                    let chartsObj = msg.charts;
                    if (typeof chartsObj === 'string') {
                        try { chartsObj = JSON.parse(chartsObj); } catch (e) { }
                    }
                    if (chartsObj && typeof chartsObj === 'object') {
                        Object.entries(chartsObj).forEach(([k, v]) => tryExtractGraph(v, k));
                    }
                }

                // 2. Check msg.graph_json, msg.graph, msg.graphs
                tryExtractGraph(msg.graph_json);
                tryExtractGraph(msg.graph);
                if (Array.isArray(msg.graphs)) {
                    msg.graphs.forEach(g => tryExtractGraph(g));
                } else if (msg.graphs && typeof msg.graphs === 'object') {
                    Object.entries(msg.graphs).forEach(([k, v]) => tryExtractGraph(v, k));
                }

                // 3. Check finalStep
                if (finalStep) {
                    tryExtractGraph(finalStep.graph_json || finalStep.graph);
                    if (finalStep.charts && typeof finalStep.charts === 'object') {
                        Object.entries(finalStep.charts).forEach(([k, v]) => tryExtractGraph(v, k));
                    }
                }

                // 4. Check all steps in msg.steps
                if (Array.isArray(msg.steps)) {
                    msg.steps.forEach(step => {
                        if (!step) return;
                        tryExtractGraph(step);
                        if (step.charts && typeof step.charts === 'object') {
                            Object.entries(step.charts).forEach(([k, v]) => tryExtractGraph(v, k));
                        }
                        if (step.content && typeof step.content === 'string' && step.content.includes('{') && step.content.includes('data')) {
                            tryExtractGraph(step.content);
                        }
                    });
                }

                // Map collected graphs to placeholders
                collectedGraphs.forEach(item => {
                    if (item.key && item.key.startsWith('CHART_')) {
                        parsedCharts[item.key] = item.graph;
                    }
                });

                foundPlaceholderKeys.forEach((pKey, idx) => {
                    if (!parsedCharts[pKey]) {
                        const candidate = collectedGraphs[idx] || collectedGraphs[0];
                        if (candidate) {
                            parsedCharts[pKey] = candidate.graph;
                        }
                    }
                });

                if (Object.keys(parsedCharts).length > 0) {
                    mappedMsg.charts = parsedCharts;
                }

                const SHOW_GRAPH = false;
                const stepGraphData = Array.isArray(msg.steps) ? msg.steps.find((s) => s?.graph_json || s?.graph)?.graph_json || msg.steps.find((s) => s?.graph_json || s?.graph)?.graph : null;
                const graphData = msg.graph_json || msg.graph || msg.graphs || stepGraphData || (msg.charts ? Object.values(msg.charts)[0]?.graph_json || Object.values(msg.charts)[0] : null);
                if (SHOW_GRAPH && graphData) {
                    try {
                        mappedMsg.graph = typeof graphData === "string" ? JSON.parse(graphData) : graphData;
                    } catch (e) {
                        console.error("Failed to parse graph JSON in history:", e);
                    }
                }

                return mappedMsg;
            });
            const historySourcesMap = {};
            rawMessages.forEach((msg) => {
                if (!msg.id) return;
                const msgId = `${msg.id}-${msg.role || "assistant"}`;
                let messageCitations = [];

                if (msg.role === "assistant" && Array.isArray(msg.steps) && msg.steps.length > 0) {
                    msg.steps.forEach((step) => {
                        if (step.tool === "rag" || step.tool === "search_interviews") return;

                        const stepCits = step.sources || step.citations;
                        if (Array.isArray(stepCits)) {
                            messageCitations.push(...stepCits);
                        }
                    });
                }

                const rawWebCitations = msg?.web_citations || msg?.citations || msg?.sources;
                if (rawWebCitations && messageCitations.length === 0) {
                    try {
                        const citations = typeof rawWebCitations === "string"
                            ? JSON.parse(rawWebCitations)
                            : rawWebCitations;
                        if (Array.isArray(citations)) {
                            messageCitations.push(...citations);
                        }
                    } catch (err) {
                        console.warn(`Failed to parse web_citations for message ${msg.id}:`, err);
                    }
                }

                if (Array.isArray(msg.steps)) {
                    msg.steps.forEach((step) => {
                        const stepSources = step?.sources || step?.citations;
                        if (step?.type === "tool_result" && Array.isArray(stepSources) && stepSources.length > 0) {
                            messageCitations.push(...stepSources);
                        }
                    });
                }

                if (messageCitations.length > 0) {
                    historySourcesMap[msgId] = messageCitations.map((s) => ({
                        title: s.title || s.filename || s.file_name || s.text || "",
                        uri: s.url || s.uri || "",
                    }));
                }
            });
            const isWebSearchEnabled =
                response?.web_search ??
                response?.data?.web_search ??
                mappedMessages.slice().reverse().find((m) => m.role === "assistant")?.is_web_search ??
                false;
            dispatch(setTalentPulseWebSearchEnabled(Boolean(isWebSearchEnabled)));


            setUIConversation(mappedMessages);
            if (mappedMessages.length > 0) {
                setIsProfilesHidden(true);
            }
            scrollToLatestViewPoint(chatEndRef, isMobile, true);
        } catch (error) {
            console.error("Error fetching Talent Pulse user conversation:", error);
        } finally {
            setIsChatLoading(false);
        }
    };


    useEffect(() => {
        const unsubscribe = subscribe("clearChat", () => {
            clearChatData();
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const unsubscribe = subscribe("thread", async (agentType, threadSessionId) => {
            if (agentType !== "TALENT_PULSE_360") return;
            if (!threadSessionId) return;

            const localSessionId = sessionManager.getSessionId("talentPulse360SessionId");
            setEditingId(null);
            if (localSessionId !== threadSessionId) {
                sessionManager.setSessionId(threadSessionId, "talentPulse360SessionId");
                resetCandidateFilters();
                fetchUserConversation(username, threadSessionId);
                stopAllVoiceAndStreaming();
            }
        });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribe, username, resetCandidateFilters]);

    useEffect(() => {
        const sessionId = sessionManager.getSessionId("talentPulse360SessionId");
        const userId = username;
        if (sessionId && userId) {
            fetchUserConversation(userId, sessionId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleScroll = () => {
        if (chatContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
            const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
            setShowScrollButton(!isAtBottom);

            // Infinite scroll check for expanded candidate grid view
            if (isCandidateProfilesSelected && isCandidateProfileGridView && scrollHeight - scrollTop - clientHeight < 300) {
                if (hasMoreProfilesRef.current && !isFetchingNextProfilesRef.current && !isLoadingRef.current) {
                    loadProfilesFromApi(profilesPageRef.current + 1, true);
                }
            }
        }
    };

    useEffect(() => {
        handleScroll();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uiConversation]);

    const handleWebConsent = async (consent) => {
        setShowWebConsent(false);

        const messageIdToUpdate = pendingConsentMessageIdRef.current;
        pendingConsentMessageIdRef.current = null;

        if (consent === CONSENT_STATUS.ACCEPTED) {
            dispatch(setTalentPulseWebSearchEnabled(true));
        } else {
            dispatch(setTalentPulseWebSearchEnabled(false));
        }

        if (messageIdToUpdate) {
            try {
                await dispatch(updateTalentPulseWebSearchConsent({ message_id: messageIdToUpdate, consent }));
            } catch (err) {
                console.error("Consent API failed:", err);
            }
        }
    };

    const handleCitationDownload = async (sessionId, fileId, fileName) => {
        const resolvedSessionId =
            sessionId ||
            sessionManager.getSessionId("talentPulse360SessionId") ||
            (typeof ensureSessionId === "function" ? ensureSessionId() : null);

        if (!resolvedSessionId || !fileId || !fileName) {
            toast.error("Citation file details are missing");
            return;
        }

        toast.info("Please wait. The file download will begin shortly.");
        try {
            const payload = { session_id: resolvedSessionId, file_id: fileId, file_name: fileName };
            const response = await dispatch(handleTalentPulseCitationDownload(payload));
            if (response && response.status !== false && !(response instanceof Error) && response.error == null) {
                const blob = new Blob([response]);
                downloadBlob(blob, fileName);
            } else {
                toast.error("Download failed");
            }
        } catch (err) {
            toast.error("Download failed");
        }
    };

    // ── Profile list content: skeletons → cards → empty state ──────────────
    const renderProfileList = () => {
        if (isProfilesLoading) {
            return Array.from({ length: 4 }, (_, i) => <ProfileCardSkeleton key={i} />);
        }
        if (!visibleCandidates.length) {
            return (
                <ProfileListEmptyState
                    title="No candidates found"
                    description="No profiles available. Try adjusting your filters or check back later."
                />
            );
        }
        return (
            <>
                {visibleCandidates.map((candidate) => (
                    <TalentPulseProfileCard
                        key={candidate.id}
                        id={candidate.id}
                        initials={candidate.initials}
                        name={candidate.name}
                        candidateId={candidate.candidateId}
                        details={candidate.details}
                        company={candidate.company}
                        status={candidate.status}
                        experience={candidate.experience}
                        source={candidate.source}
                        profile={candidate.profile}
                        checked={selectedCards.includes(normalizeCandidateId(candidate.id))}
                        onCheckChange={handleCardCheck}
                        onViewProfile={handleFetchProfileDetail}
                    />
                ))}
                {isFetchingNextProfiles && Array.from({ length: 3 }, (_, i) => <ProfileCardSkeleton key={`next-${i}`} />)}
            </>
        );
    };

    return (
        <div className={`upl-genie-page-wrapper centered-cpc-page ${hasChatMessages ? "is-active" : "is-empty"}`}>

            <div className="upl-genie-container talent-pulse-flex-container">
                <LoadingScreen isLoading={isChatLoading} />
                {!isChatLoading && (
                    <>
                        {isCandidateProfilesSelected && (
                            <TalentPulseFilterBar
                                type={filterType}
                                onToggleType={withUnhide(setFilterType)}
                                profileTypes={[
                                    { id: 'all', label: 'All' },
                                    ...(CANDIDATE_PROFILES_ENABLED ? [{ id: 'external', label: 'External' }] : [])
                                ]}
                                roles={roles}
                                selectedRole={selectedRole}
                                onRoleChange={withUnhide(setSelectedRole)}
                                companies={companies}
                                selectedCompany={selectedCompany}
                                onCompanyChange={withUnhide(setSelectedCompany)}
                                regions={regions}
                                selectedRegion={selectedRegion}
                                onRegionChange={withUnhide(setSelectedRegion)}
                                states={states}
                                selectedState={selectedState}
                                onStateChange={withUnhide(handleStateChange)}
                                territories={territories}
                                selectedTerritory={selectedTerritory}
                                onTerritoryChange={withUnhide(setSelectedTerritory)}
                                sort={filterSort}
                                onSortChange={withUnhide(setFilterSort)}
                                compareCount={selectedCards.length}
                                compareDisabled={selectedCards.length < 2}
                                onCompareClick={handleCompareProfiles}
                                isProfilesHidden={isProfilesHidden}
                                onToggleProfilesClick={() => setIsProfilesHidden((prev) => !prev)}
                                isGridView={isCandidateProfileGridView}
                                onToggleGridView={() => setIsCandidateProfileGridView((isGridView) => !isGridView)}
                                candidateCount={filteredCandidates.length}
                                onClearFilters={resetCandidateFilters}
                                isCandidateProfilesSelected={isCandidateProfilesSelected}
                                profileSearchQuery={profileSearchQuery}
                                onProfileSearchChange={withUnhide(setProfileSearchQuery)}
                                onProfileSearchClear={withUnhide(() => setProfileSearchQuery(''))}
                                totalProfilesCount={totalProfilesCount}
                                searchLower={searchLower}
                                visibleCandidatesCount={visibleCandidates.length}
                                isProfilesLoading={isProfilesLoading}
                                filtersDisabled={isCompareActive}
                                isCompareActive={isCompareActive}
                                onResetClick={handleCompareReset}
                            />
                        )}
                        <div className={`talent-pulse-main-layout ${!isCandidateProfilesSelected ? "normal-chat-layout" : ""}`}>
                            {isCandidateProfilesSelected && !isCandidateProfileGridView && (
                                <div className={`talent-pulse-slider-wrap ${isProfilesHidden ? 'is-collapsed' : ''}`}>
                                    <div className="talent-pulse-slider-rail" ref={sliderRailRef} onScroll={syncSliderScrollbar}>
                                        {renderProfileList()}
                                    </div>

                                    {sliderScrollbar.visible && (
                                        <button
                                            type="button"
                                            className="tps-scrollbar"
                                            aria-label="Scroll candidate profiles"
                                            onClick={(event) => {
                                                const rail = sliderRailRef.current;
                                                if (!rail) return;
                                                const bounds = event.currentTarget.getBoundingClientRect();
                                                const position = (event.clientX - bounds.left) / bounds.width;
                                                rail.scrollTo({ left: position * (rail.scrollWidth - rail.clientWidth), behavior: 'smooth' });
                                            }}
                                        >
                                            <span style={{ width: `${sliderScrollbar.width}%`, left: `${sliderScrollbar.offset}%` }} />
                                        </button>
                                    )}
                                </div>
                            )}

                            <div className="upl-chat-body" ref={chatContainerRef} onScroll={handleScroll}>
                                <div className="upl-chat-content-column">


                                    {!hasChatMessages && !isChatLoading && !isCandidateProfilesSelected && (
                                        <div className="upl-new-chat-content talent-pulse-content">
                                            <Dashboard
                                                icon={talentPulse360Icon}
                                                title={`${constants.WELCOME}, ${fullName}!`}
                                                subtitle={constants.TALENT_PULSE_SUBTITLE}
                                                cardsData={talentPulse360CardsData}
                                                cardGradient={true}
                                                onPromptClick={handlePromptClick}
                                            />
                                        </div>
                                    )}

                                    {!hasChatMessages && !isChatLoading && isCandidateProfilesSelected && !isCandidateProfileGridView && (
                                        <div className="talent-pulse-empty-workspace">
                                            <img src={talentPulse360Icon} alt="Talent Pulse 360" />
                                            <p>{constants.TALENT_PULSE_EMPTY_WORKSPACE_DESCRIPTION}</p>
                                        </div>
                                    )}

                                    {hasChatMessages && !isCandidateProfileGridView && (
                                        <div className="upl-messages-list talent-pulse-messages-list">
                                            {uiConversation.map((pair, index) => {
                                                const isLastMessage = index === uiConversation.length - 1;
                                                const voiceText = pair.role === "assistant"
                                                    ? extractFinalResponseFromTalentPulse(pair.content, pair.steps, pair.rawContent)
                                                    : pair.content;
                                                const pairForVoice = {
                                                    ...pair,
                                                    graph: pair.graph || pair.graphs || null,
                                                    content: (pair.role === "assistant" && voiceText) ? voiceText : pair.content,
                                                };
                                                return (
                                                    <MessageBox
                                                        key={pair.id || index}
                                                        pair={pairForVoice}
                                                        index={index}
                                                        editingId={editingId}
                                                        editedText={editedText}
                                                        setEditedText={setEditedText}
                                                        isMobile={isMobile}
                                                        fullname={fullName}
                                                        isTextBoxDisabled={isLoading}
                                                        showEditPrompt={false}
                                                        handleEditClick={handleEditClick}
                                                        handleSave={handleSave}
                                                        setEditingId={setEditingId}
                                                        botIcon={talentPulse360Icon}
                                                        isLoading={isLoading}
                                                        isLastMessage={isLastMessage}
                                                        useMathJax={false}
                                                        charts={pair.charts}
                                                        renderContent={(messagePair) => renderTalentPulseContent({
                                                            content: messagePair.content === "Loading..." ? "" : messagePair.content,
                                                            citations: messagePair.citations,
                                                            steps: messagePair.steps,
                                                            rawContent: messagePair.rawContent,
                                                            charts: messagePair.charts,
                                                            isStreaming: (isLoading && isLastMessage) || !!messagePair.isPlaceholder,
                                                            handleCitationDownload: (sId, fId, fName) =>
                                                                handleCitationDownload(
                                                                    sId || messagePair.sessionId || messagePair.session_id || sessionManager.getSessionId("talentPulse360SessionId"),
                                                                    fId,
                                                                    fName
                                                                ),
                                                        })}
                                                        showFeedback={{ option: ["like", "dislike", "copy"], moreOption: [] }}
                                                        onFeedback={handleFeedback}
                                                        feedbackState={pair.feedback}
                                                        showTimeStamp={true}
                                                        hideMetadata={true}
                                                        sourceLinks={[]}
                                                        showProgressSteps={pair.role === "assistant" && pair.steps?.length > 0}
                                                        enableVoiceOutput={!isCandidateProfilesSelected}
                                                        autoPlayVoice={!isCandidateProfilesSelected && isLastMessage && (autoPlayLastMessage || !!pair.autoPlayVoice)}
                                                        isStreamingVop={!isCandidateProfilesSelected}
                                                        streamingVopWsUrl={getSpeechWsUrl("tts", "talentPulse")}
                                                        graphVariant="compact"
                                                        onVoiceOutputStart={
                                                            !isCandidateProfilesSelected && isLastMessage && (autoPlayLastMessage || !!pair.autoPlayVoice)
                                                                ? alexa.onVoiceOutputStart
                                                                : undefined
                                                        }
                                                        onVoiceOutputEnd={
                                                            !isCandidateProfilesSelected && isLastMessage && (autoPlayLastMessage || !!pair.autoPlayVoice) && !showWebConsent
                                                                ? alexa.onVoiceOutputEnd
                                                                : undefined
                                                        }
                                                        renderExtras={() => {
                                                            const hasSteps = pair.role === "assistant" && Array.isArray(pair.steps) && pair.steps.length > 0;
                                                            if (hasSteps) return null;
                                                            return renderCitationPanels(
                                                                getNonTranscriptCitations(pair.citations),
                                                                (sId, fId, fName) =>
                                                                    handleCitationDownload(
                                                                        sId || pair.sessionId || pair.session_id || sessionManager.getSessionId("talentPulse360SessionId"),
                                                                        fId,
                                                                        fName
                                                                    )
                                                            );
                                                        }}
                                                    />
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Render vertical grid view of candidates when enabled inside the scrollable chat container */}
                                    {isCandidateProfilesSelected && isCandidateProfileGridView && (
                                        <div className={`talent-pulse-slider-wrap is-expanded-view ${isProfilesHidden ? 'is-collapsed' : ''}`}>
                                            <div className="talent-pulse-slider-rail">
                                                {isProfilesLoading ? (
                                                    <>
                                                        <ProfileCardSkeleton />
                                                        <ProfileCardSkeleton />
                                                        <ProfileCardSkeleton />
                                                        <ProfileCardSkeleton />
                                                    </>
                                                ) : visibleCandidates.length ? (
                                                    <>
                                                        {visibleCandidates.map((candidate) => (
                                                            <TalentPulseProfileCard
                                                                key={candidate.id}
                                                                id={candidate.id}
                                                                initials={candidate.initials}
                                                                name={candidate.name}
                                                                candidateId={candidate.candidateId}
                                                                details={candidate.details}
                                                                company={candidate.company}
                                                                status={candidate.status}
                                                                experience={candidate.experience}
                                                                source={candidate.source}
                                                                profile={candidate.profile}
                                                                checked={selectedCards.includes(normalizeCandidateId(candidate.id))}
                                                                onCheckChange={handleCardCheck}
                                                                onViewProfile={handleFetchProfileDetail}
                                                            />
                                                        ))}
                                                        {isFetchingNextProfiles && (
                                                            <>
                                                                <ProfileCardSkeleton />
                                                                <ProfileCardSkeleton />
                                                                <ProfileCardSkeleton />
                                                            </>
                                                        )}
                                                    </>
                                                ) : (
                                                    <ProfileListEmptyState
                                                        title="No candidates found"
                                                        description="No profiles available. Try adjusting your filters or check back later."
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div ref={chatEndRef} />
                                </div>
                            </div>
                        </div>

                        <div className="upl-genie-footer">
                            <div className="upl-chat-content-column">
                                {hasChatMessages && showScrollButton && (
                                    <div className="upl-scroll-btn-row">
                                        <button
                                            className="upl-scroll-bottom-btn"
                                            onClick={() => scrollToLatestViewPoint(chatEndRef, isMobile, true, true)}
                                        >
                                            <img src={downArrow} alt="Scroll to bottom" />
                                        </button>
                                    </div>
                                )}
                                <ChatPromptContainer
                                    ref={chatPromptContainerRef}
                                    value={message}
                                    onChange={handleInputChange}
                                    isLoading={isLoading}
                                    placeholder="Ask anything..."
                                    handleSendMessage={handleSendMessage}
                                    richInputRef={richInputRef}
                                    showVoiceInput={!isCandidateProfilesSelected}
                                    showTalentPulseWebSearch={true}
                                    showStopStreaming={false}
                                    handleStopStreaming={stopStreaming}
                                    isVoiceStopFeature={true}
                                    theme="talent-pulse"
                                    charLimit={UPL_GENIE_PROMPT_CHAR_LIMIT}
                                    onVoiceStart={handleVoiceStart}
                                    onVoiceKeyword={handleVoiceKeyword}
                                    onVoiceStop={handleVoiceStop}
                                    keepListeningWhileLoading={keepListeningWhileLoading}
                                    voiceWsUrl={getSpeechWsUrl("transcribe", "talentPulse")}
                                    contentPulseChip={contentPulseChipConfig}
                                    selectedCandidateChip={selectedCandidateChipConfig}
                                    profileTypeChip={profileTypeChipConfig}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            {showWebConsent && (
                <Modal open={showWebConsent} onClose={() => setShowWebConsent(false)}>
                    <div className="modal-inner-content">
                        <div className="modal-title">{constants.WEB_SEARCH_CONFIRM_TITLE || "Web Search Confirmation"}</div>
                        <div className="modal-message">
                            {constants.ROUTER_DISABLED_WEB_SEARCH_CONFIRM_MESSAGE || "For more better result can we send data to web search?"}
                        </div>
                        <div className="modal-buttons">
                            <button
                                className="button-style primary-grey-button"
                                onClick={() => handleWebConsent(CONSENT_STATUS.DISMISSED)}
                            >
                                {constants.CANCEL}
                            </button>
                            <button
                                className="button-style primary-button"
                                onClick={() => handleWebConsent(CONSENT_STATUS.ACCEPTED)}
                            >
                                {constants.CONFIRM}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default TalentPulse360;
